// CPT Code Category Tagging Map
// This is the internal mapping for modifier filtering logic
// Can be extended/overridden by LINK Ops in the database

import type { CptTagMapEntry, CptCategory, SiteOfService } from './types';

// Seed data for common orthopedic CPT codes
// NOTE: This is the baseline seed. LINK Ops can extend via database overrides.
// Internal labels only - no CPT copyrighted descriptions stored.
export const CPT_TAG_MAP: Record<string, CptTagMapEntry> = {
  // ═══════════════════════════════════════════════════════════════════════════
  // KNEE ARTHROSCOPY / MENISCUS / CHONDROPLASTY
  // ═══════════════════════════════════════════════════════════════════════════
  '29881': {
    cpt_code: '29881',
    category: 'SURGICAL',
    laterality_required: true,
    allows_assistant: true,
    allows_26_tc: false,
    notes_internal: 'Knee scope - meniscectomy',
  },
  '29882': {
    cpt_code: '29882',
    category: 'SURGICAL',
    laterality_required: true,
    allows_assistant: true,
    allows_26_tc: false,
    notes_internal: 'Knee scope - meniscus repair',
  },
  '29877': {
    cpt_code: '29877',
    category: 'SURGICAL',
    laterality_required: true,
    allows_assistant: true,
    allows_26_tc: false,
    notes_internal: 'Knee scope - chondroplasty',
  },
  '29880': {
    cpt_code: '29880',
    category: 'SURGICAL',
    laterality_required: true,
    allows_assistant: true,
    allows_26_tc: false,
    notes_internal: 'Knee scope - meniscectomy medial/lateral',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // ACL / PCL RECONSTRUCTION
  // ═══════════════════════════════════════════════════════════════════════════
  '29888': {
    cpt_code: '29888',
    category: 'SURGICAL',
    laterality_required: true,
    allows_assistant: true,
    allows_26_tc: false,
    notes_internal: 'ACL reconstruction',
  },
  '29889': {
    cpt_code: '29889',
    category: 'SURGICAL',
    laterality_required: true,
    allows_assistant: true,
    allows_26_tc: false,
    notes_internal: 'PCL reconstruction',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // SHOULDER ARTHROSCOPY
  // ═══════════════════════════════════════════════════════════════════════════
  '29827': {
    cpt_code: '29827',
    category: 'SURGICAL',
    laterality_required: true,
    allows_assistant: true,
    allows_26_tc: false,
    notes_internal: 'Shoulder scope - RTC repair',
  },
  '29826': {
    cpt_code: '29826',
    category: 'SURGICAL',
    laterality_required: true,
    allows_assistant: true,
    allows_26_tc: false,
    notes_internal: 'Shoulder scope - SAD/acromioplasty',
  },
  '29823': {
    cpt_code: '29823',
    category: 'SURGICAL',
    laterality_required: true,
    allows_assistant: true,
    allows_26_tc: false,
    notes_internal: 'Shoulder scope - extensive debridement',
  },
  '29824': {
    cpt_code: '29824',
    category: 'SURGICAL',
    laterality_required: true,
    allows_assistant: true,
    allows_26_tc: false,
    notes_internal: 'Shoulder scope - distal clavicle excision',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // LABRUM / SLAP REPAIR
  // ═══════════════════════════════════════════════════════════════════════════
  '29806': {
    cpt_code: '29806',
    category: 'SURGICAL',
    laterality_required: true,
    allows_assistant: true,
    allows_26_tc: false,
    notes_internal: 'Shoulder scope - capsulorrhaphy',
  },
  '29807': {
    cpt_code: '29807',
    category: 'SURGICAL',
    laterality_required: true,
    allows_assistant: true,
    allows_26_tc: false,
    notes_internal: 'Shoulder scope - SLAP repair',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // HIP ARTHROSCOPY
  // ═══════════════════════════════════════════════════════════════════════════
  '29914': {
    cpt_code: '29914',
    category: 'SURGICAL',
    laterality_required: true,
    allows_assistant: true,
    allows_26_tc: false,
    notes_internal: 'Hip scope - femoroplasty',
  },
  '29915': {
    cpt_code: '29915',
    category: 'SURGICAL',
    laterality_required: true,
    allows_assistant: true,
    allows_26_tc: false,
    notes_internal: 'Hip scope - acetabuloplasty',
  },
  '29916': {
    cpt_code: '29916',
    category: 'SURGICAL',
    laterality_required: true,
    allows_assistant: true,
    allows_26_tc: false,
    notes_internal: 'Hip scope - labral repair',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // UPPER EXTREMITY - NERVE DECOMPRESSION
  // ═══════════════════════════════════════════════════════════════════════════
  '64721': {
    cpt_code: '64721',
    category: 'SURGICAL',
    laterality_required: true,
    allows_assistant: false,
    allows_26_tc: false,
    notes_internal: 'Carpal tunnel release',
  },
  '64718': {
    cpt_code: '64718',
    category: 'SURGICAL',
    laterality_required: true,
    allows_assistant: false,
    allows_26_tc: false,
    notes_internal: 'Cubital tunnel release',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // HAND - TRIGGER FINGER
  // ═══════════════════════════════════════════════════════════════════════════
  '26055': {
    cpt_code: '26055',
    category: 'SURGICAL',
    laterality_required: true,
    allows_assistant: false,
    allows_26_tc: false,
    notes_internal: 'Trigger finger release',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // TOTAL JOINT ARTHROPLASTY - MAJOR
  // ═══════════════════════════════════════════════════════════════════════════
  '27130': {
    cpt_code: '27130',
    category: 'SURGICAL',
    laterality_required: true,
    allows_assistant: true,
    allows_26_tc: false,
    notes_internal: 'THA - total hip',
  },
  '27447': {
    cpt_code: '27447',
    category: 'SURGICAL',
    laterality_required: true,
    allows_assistant: true,
    allows_26_tc: false,
    notes_internal: 'TKA - total knee',
  },
  '23472': {
    cpt_code: '23472',
    category: 'SURGICAL',
    laterality_required: true,
    allows_assistant: true,
    allows_26_tc: false,
    notes_internal: 'TSA - total shoulder',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // SPINE - NO LATERALITY
  // ═══════════════════════════════════════════════════════════════════════════
  '22551': {
    cpt_code: '22551',
    category: 'SURGICAL',
    laterality_required: false,
    allows_assistant: true,
    allows_26_tc: false,
    notes_internal: 'ACDF - cervical fusion',
  },
  '22612': {
    cpt_code: '22612',
    category: 'SURGICAL',
    laterality_required: false,
    allows_assistant: true,
    allows_26_tc: false,
    notes_internal: 'Posterior lumbar fusion',
  },
  '22630': {
    cpt_code: '22630',
    category: 'SURGICAL',
    laterality_required: false,
    allows_assistant: true,
    allows_26_tc: false,
    notes_internal: 'Lumbar interbody fusion',
  },
  '22845': {
    cpt_code: '22845',
    category: 'SURGICAL',
    laterality_required: false,
    allows_assistant: true,
    allows_26_tc: false,
    notes_internal: 'Anterior instrumentation 2-3 levels',
  },
  '63030': {
    cpt_code: '63030',
    category: 'SURGICAL',
    laterality_required: false,
    allows_assistant: true,
    allows_26_tc: false,
    notes_internal: 'Lumbar discectomy',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // HAND / WRIST
  // ═══════════════════════════════════════════════════════════════════════════
  '25607': {
    cpt_code: '25607',
    category: 'SURGICAL',
    laterality_required: true,
    allows_assistant: true,
    allows_26_tc: false,
    notes_internal: 'Distal radius ORIF',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // IMAGING - MRI
  // ═══════════════════════════════════════════════════════════════════════════
  '73221': {
    cpt_code: '73221',
    category: 'IMAGING',
    laterality_required: true,
    allows_assistant: false,
    allows_26_tc: true,
    notes_internal: 'MRI upper extremity joint',
  },
  '73721': {
    cpt_code: '73721',
    category: 'IMAGING',
    laterality_required: true,
    allows_assistant: false,
    allows_26_tc: true,
    notes_internal: 'MRI lower extremity joint',
  },
  '72148': {
    cpt_code: '72148',
    category: 'IMAGING',
    laterality_required: false,
    allows_assistant: false,
    allows_26_tc: true,
    notes_internal: 'MRI lumbar spine',
  },
  '73722': {
    cpt_code: '73722',
    category: 'IMAGING',
    laterality_required: true,
    allows_assistant: false,
    allows_26_tc: true,
    notes_internal: 'MRI lower extremity w/contrast',
  },
  '77002': {
    cpt_code: '77002',
    category: 'IMAGING',
    laterality_required: false,
    allows_assistant: false,
    allows_26_tc: true,
    notes_internal: 'Fluoro guidance needle',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // E&M - EVALUATION & MANAGEMENT
  // ═══════════════════════════════════════════════════════════════════════════
  '99203': {
    cpt_code: '99203',
    category: 'EVAL_MGMT',
    laterality_required: false,
    allows_assistant: false,
    allows_26_tc: false,
    notes_internal: 'New patient - low',
  },
  '99204': {
    cpt_code: '99204',
    category: 'EVAL_MGMT',
    laterality_required: false,
    allows_assistant: false,
    allows_26_tc: false,
    notes_internal: 'New patient - moderate',
  },
  '99213': {
    cpt_code: '99213',
    category: 'EVAL_MGMT',
    laterality_required: false,
    allows_assistant: false,
    allows_26_tc: false,
    notes_internal: 'Established patient - low',
  },
  '99214': {
    cpt_code: '99214',
    category: 'EVAL_MGMT',
    laterality_required: false,
    allows_assistant: false,
    allows_26_tc: false,
    notes_internal: 'Established patient - moderate',
  },
  '99215': {
    cpt_code: '99215',
    category: 'EVAL_MGMT',
    laterality_required: false,
    allows_assistant: false,
    allows_26_tc: false,
    notes_internal: 'Established patient - high',
  },
  '99205': {
    cpt_code: '99205',
    category: 'EVAL_MGMT',
    laterality_required: false,
    allows_assistant: false,
    allows_26_tc: false,
    notes_internal: 'New patient - high',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // OFFICE PROCEDURES - INJECTIONS
  // ═══════════════════════════════════════════════════════════════════════════
  '20610': {
    cpt_code: '20610',
    category: 'OFFICE_PROC',
    laterality_required: true,
    allows_assistant: false,
    allows_26_tc: false,
    notes_internal: 'Major joint injection/aspiration',
  },
  '20605': {
    cpt_code: '20605',
    category: 'OFFICE_PROC',
    laterality_required: true,
    allows_assistant: false,
    allows_26_tc: false,
    notes_internal: 'Intermediate joint injection',
  },
  '20600': {
    cpt_code: '20600',
    category: 'OFFICE_PROC',
    laterality_required: true,
    allows_assistant: false,
    allows_26_tc: false,
    notes_internal: 'Small joint injection',
  },
};

/**
 * Get CPT tag entry, with fallback logic for unknown CPTs
 */
export function getCptTag(
  cptCode: string,
  siteOfService?: SiteOfService
): CptTagMapEntry {
  // Check explicit mapping first
  if (CPT_TAG_MAP[cptCode]) {
    return CPT_TAG_MAP[cptCode];
  }
  
  // Apply default rules based on CPT pattern and site of service
  const category = inferCategory(cptCode, siteOfService);
  const lateralityRequired = inferLateralityRequired(cptCode, category);
  
  return {
    cpt_code: cptCode,
    category,
    laterality_required: lateralityRequired,
    allows_assistant: category === 'SURGICAL',
    allows_26_tc: category === 'IMAGING',
    notes_internal: 'Auto-inferred, requires LINK review',
  };
}

/**
 * Infer category from CPT code pattern and site of service
 */
function inferCategory(cptCode: string, siteOfService?: SiteOfService): CptCategory {
  const code = parseInt(cptCode, 10);
  
  // E&M codes: 99000-99499
  if (code >= 99000 && code <= 99499) {
    return 'EVAL_MGMT';
  }
  
  // Radiology: 70000-79999
  if (code >= 70000 && code <= 79999) {
    return 'IMAGING';
  }
  
  // Surgery codes: 10000-69999
  if (code >= 10000 && code <= 69999) {
    // If site is hospital or ASC, likely surgical
    if (siteOfService === 'hospital' || siteOfService === 'asc') {
      return 'SURGICAL';
    }
    // If office, could be office procedure
    if (siteOfService === 'office') {
      // Minor procedures often done in office
      if (code >= 10000 && code <= 19999) return 'OFFICE_PROC'; // Integumentary
      if (code >= 20000 && code <= 29999) return 'SURGICAL'; // Musculoskeletal
    }
    return 'SURGICAL';
  }
  
  return 'UNKNOWN';
}

/**
 * Infer if laterality is required based on CPT code
 */
function inferLateralityRequired(cptCode: string, category: CptCategory): boolean {
  // Most surgical and imaging codes on extremities require laterality
  if (category === 'SURGICAL' || category === 'IMAGING') {
    const code = parseInt(cptCode, 10);
    // Orthopedic surgery range (musculoskeletal system)
    if (code >= 20000 && code <= 29999) return true;
    // Upper extremity imaging
    if (code >= 73000 && code <= 73225) return true;
    // Lower extremity imaging  
    if (code >= 73500 && code <= 73725) return true;
  }
  return false;
}

/**
 * Check if a CPT code is explicitly tagged or requires review
 */
export function isCptExplicitlyTagged(cptCode: string): boolean {
  return cptCode in CPT_TAG_MAP;
}
